class RelatedProducts extends HTMLElement {
  constructor() {
    super();
    this.activeType = 'product-recommendations';
    this.querySelectorAll('button[data-show]').forEach((toggleButton) => {
      toggleButton.addEventListener('click', this.onToggleClick.bind(this));
      toggleButton.setAttribute('aria-selected', toggleButton.dataset.show === this.activeType);
    });
  }

  onToggleClick(event) {
    this.querySelectorAll('button[data-show]').forEach((button) => {
      button.setAttribute('aria-selected', button === event.currentTarget ? 'true' : 'false');
    });
    this.classList.remove(`active-${this.activeType}`);
    this.querySelector(`[data-type="${this.activeType}"]`).setAttribute('aria-hidden', true);
    this.activeType = event.currentTarget.dataset.show;
    this.classList.add(`active-${this.activeType}`);
    this.querySelector(`[data-type="${this.activeType}"]`).setAttribute('aria-hidden', false);
  }
}

customElements.define('related-products', RelatedProducts);

class ProductCarousel extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {}

  bindEvents() {
    this.prevButton = this.querySelector('button[data-direction="previous"]');
    this.nextButton = this.querySelector('button[data-direction="next"]');

    this.carousel = this.querySelector('.carousel');
    this.carouselItems = this.carousel.querySelectorAll('.carousel-item');

    this.initPages();

    this.carouselFirstItemNode = this.carouselItems[0];
    this.carousel.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.carousel);
  }

  initPages() {
    this.carouselItemsToShow = Array.from(this.carouselItems).filter((element) => element.clientWidth > 0);
    if (this.carouselItemsToShow.length < 2) return;
    this.carouselItemOffset = this.carouselItemsToShow[1].offsetLeft - this.carouselItemsToShow[0].offsetLeft;
    this.itemsPerPage = Math.floor(
      (this.carousel.clientWidth - this.carouselItemsToShow[0].offsetLeft) / this.carouselItemOffset,
    );
    this.totalPages = this.carouselItemsToShow.length - this.itemsPerPage + 1;

    this.update();
  }

  update() {
    if (this.prevButton) {
      if (this.isSlideVisible(this.carouselItems[0]) && this.carousel.scrollLeft < this.carouselItemOffset) {
        this.prevButton.setAttribute('disabled', 'disabled');
        this.prevButton.classList.add('opacity-50');
      } else {
        this.prevButton.removeAttribute('disabled');
        this.prevButton.classList.remove('opacity-50');
      }
    }
    if (this.nextButton) {
      if (this.isSlideVisible(this.carouselItems[this.carouselItems.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
        this.nextButton.classList.add('opacity-50');
      } else {
        this.nextButton.removeAttribute('disabled');
        this.nextButton.classList.remove('opacity-50');
      }
    }

    this.carouselItems.forEach((item, index) => {
      if (this.isSlideVisible(item)) {
        item.setAttribute('aria-hidden', 'false');
        item.querySelectorAll('a, button, [tabindex]').forEach((focusable) => {
          focusable.removeAttribute('tabindex');
        });
      } else {
        item.setAttribute('aria-hidden', 'true');
        item.querySelectorAll('a, button, [tabindex]').forEach((focusable) => {
          focusable.setAttribute('tabindex', '-1');
        });
      }
    });
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = 1;

    this.carouselScrollPosition =
      event.currentTarget.name === 'next'
        ? this.carousel.scrollLeft + step * this.carouselItemOffset
        : this.carousel.scrollLeft - step * this.carouselItemOffset;

    this.setSlidePosition(this.carouselScrollPosition);
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.carousel.clientWidth + this.carousel.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.carousel.scrollLeft
    );
  }

  setSlidePosition(position) {
    this.carousel.scrollTo({
      left: position,
    });
  }
}

class ProductRecommendations extends ProductCarousel {
  constructor() {
    super();
    this.loaded = false;
  }

  loadRecommendations(text) {
    const html = document.createElement('div');
    html.innerHTML = text;
    const recommendations = html.querySelector('product-recommendations');

    if (recommendations && recommendations.innerHTML.trim().length) {
      this.innerHTML = recommendations.innerHTML;
      console.log('calling from recommendations');
      setTimeout(() => {
        this.bindEvents();
      }, 300);
    }
  }

  connectedCallback() {
    const fetchRecommendations = () => {
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          this.loadRecommendations(text);
        })
        .catch((e) => {
          console.error(e);
        });
    };

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      if (this.loaded) return;

      fetchRecommendations();
    };

    this.observer = new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);

    let onScroll = (ev) => {
      if (window.scrollY > 0) {
        this.observer.disconnect();
        this.loaded = true;
        fetchRecommendations();
      }
    };

    if (window.scrollY > 0) {
      onScroll();
    } else {
      window.addEventListener('scroll', onScroll.bind(this), { once: true });
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class RecentlyViewed extends ProductCarousel {
  constructor() {
    super();
    this.productHandle = this.dataset.productHandle;
    this.productGrid = this.querySelector('ul');
    this.productHtml = '';
  }

  connectedCallback() {
    this.init();
  }

  async init() {
    this.processRecentlyViewed()
      .then(() => {
        this.productGrid.innerHTML = this.productHtml;
        this.bindEvents();
      })
      .catch((error) => {
        console.error('Error processing recently viewed:', error);
      });
  }

  async processRecentlyViewed() {
    this.recentlyViewedHandles = await this.getRecentlyViewed();
    if (this.recentlyViewedHandles.length < 1) return;
    this.classList.remove('hidden');
    const triggerButton = document.querySelector('button[data-show="recently-viewed"]');
    triggerButton.classList.remove('hidden');
    let loopIndex = 1;

    if (this.recentlyViewedHandles.length > 4) {
      this.querySelector('[data-navigation]').classList.remove('!hidden');
    } else {
      this.querySelector('[data-navigation]').classList.add('!hidden');
    }

    const handles = this.recentlyViewedHandles.slice(0, 12);
    const searchParams = handles.map((handle) => `handle:${handle}`);
    const q = searchParams.join(' OR ');
    const url = `/search?section_id=search-recent&type=product&q=${encodeURIComponent(q)}`;

    await fetch(url)
      .then((response) => response.text())
      .then((response) => {
        const html = new DOMParser().parseFromString(response, 'text/html');
        for (const handle of handles) {
          const product = html.querySelector(`[data-product-handle="${handle}"]`);
          if (product) {
            this.productHtml += product.outerHTML;
          }
        }
      });

    return true;
  }

  async getRecentlyViewed() {
    var viewedProducts = [];
    var viewedProductsString = localStorage.getItem('recently_viewed_products');
    if (viewedProductsString) {
      viewedProducts = JSON.parse(viewedProductsString);
    }
    var index = viewedProducts.indexOf(this.productHandle);
    if (index) {
      viewedProducts.splice(index, 1);
    }
    return viewedProducts;
  }
}

customElements.define('recently-viewed', RecentlyViewed);
